import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { useCart } from '@/context/CartContext';
import { useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2 } from 'lucide-react';

export default function CartSheet() {
  const { items, isOpen, setIsOpen, removeItem, updateQuantity, totalPrice, clearCart } = useCart();
  const navigate = useNavigate();

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent side="right" className="bg-white w-full sm:max-w-md p-0" style={{ borderLeft: '1px solid rgba(12,74,140,0.08)' }} data-testid="cart-sheet">
        <SheetHeader className="p-6" style={{ borderBottom: '1px solid rgba(12,74,140,0.06)' }}>
          <SheetTitle className="font-serif text-xl text-[#0C4A8C] font-medium">Your Cart</SheetTitle>
          <SheetDescription className="text-[#6B7D8A] text-sm font-light">
            {items.length === 0 ? 'Your cart is empty.' : `${items.length} item${items.length > 1 ? 's' : ''}`}
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col h-[calc(100vh-180px)]">
          <div className="flex-1 overflow-y-auto p-6">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <p className="text-[#6B7D8A] text-sm font-light">Nothing here yet.</p>
              </div>
            ) : (
              <div className="flex flex-col">
                {items.map(item => (
                  <div key={item.id} data-testid={`cart-item-${item.id}`} className="flex gap-4 py-4" style={{ borderBottom: '1px solid rgba(12,74,140,0.06)' }}>
                    <div className="w-14 h-14 bg-[#F0F9FF] rounded-[2px] overflow-hidden flex-shrink-0">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm text-[#0C4A8C] font-sans font-normal truncate">{item.name}</h4>
                      <p className="text-[11px] text-[#6B7D8A] mt-0.5">{item.size}</p>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-2">
                          <button data-testid={`cart-decrease-${item.id}`} onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="w-6 h-6 border rounded-[2px] flex items-center justify-center text-[#6B7D8A] hover:text-[#0C4A8C] hover:border-[#0C4A8C] transition-colors" style={{ borderColor: 'rgba(12,74,140,0.1)' }}>
                            <Minus size={10} />
                          </button>
                          <span className="text-sm text-[#0C4A8C] font-sans w-3 text-center">{item.quantity}</span>
                          <button data-testid={`cart-increase-${item.id}`} onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="w-6 h-6 border rounded-[2px] flex items-center justify-center text-[#6B7D8A] hover:text-[#0C4A8C] hover:border-[#0C4A8C] transition-colors" style={{ borderColor: 'rgba(12,74,140,0.1)' }}>
                            <Plus size={10} />
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-[#0C4A8C] font-sans">&#8377;{item.price * item.quantity}</span>
                          <button data-testid={`cart-remove-${item.id}`} onClick={() => removeItem(item.id)} className="text-[#6B7D8A] hover:text-red-500 transition-colors"><Trash2 size={13} /></button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {items.length > 0 && (
            <div className="p-6" style={{ borderTop: '1px solid rgba(12,74,140,0.06)' }}>
              <div className="flex items-center justify-between mb-4">
                <span className="eyebrow text-[#6B7D8A]">Total</span>
                <span data-testid="cart-total" className="text-lg text-[#0C4A8C] font-serif font-medium">&#8377;{totalPrice}</span>
              </div>
              {totalPrice < 499 && <p className="text-[11px] text-[#0C7BC0] mb-3">Add &#8377;{499 - totalPrice} more for free shipping</p>}
              <button data-testid="checkout-button" onClick={() => { setIsOpen(false); navigate('/checkout'); }} className="w-full bg-[#0C4A8C] text-[#F0F9FF] py-3 rounded-[2px] text-[11px] font-sans uppercase tracking-[3px] hover:bg-[#093B70] transition-colors">Checkout</button>
              <button data-testid="clear-cart-button" onClick={clearCart} className="w-full text-[#6B7D8A] text-[11px] mt-2 hover:text-[#0C4A8C] transition-colors uppercase tracking-[2px]">Clear Cart</button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
